import "./SideNavigationPanel.scss";
import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store";
import { useAcademyLink } from "../../Academy/useAcademyLink";
import { IconPerfIncome } from "../../../common/icons/IconPerfIncome/IconPerfIncome";
import { IconTwoUsersNav } from "../../../common/icons/IconTwoUsersNav/IconTwoUsersNav";
import { IconAcademy } from "../../../common/icons/IconAcademy/IconAcademy";
import { IconAmwayLogo } from "../../../common/icons/IconAmwayLogo/IconAmwayLogo";
import { IconChevronDown } from "../../../common/icons/IconChevronDown/IconChevronDown";
import { NavExpandIcon } from "../NavExpandIcon/NavExpandIcon";
import { setSideNavExpanded } from "../../../reducers/uiState";
import { CategoryFlyoutMenu } from "../CategoryFlyoutMenu/CategoryFlyoutMenu";
import { CategoryAccordion } from "../CategoryAccordion/CategoryAccordion";
import type { AccordionMenuItem } from "../CategoryAccordion/CategoryAccordion";
import { createNavigationConfig } from "../../../common/util/Navigation/createNavigationConfig";
import type { NavigationApiResponse } from "../../../services/MyBizShell/mybizShellAPI.types";
import defaultContent from "../../../content/navigation";
import { NavigationSkeletonLoader } from "./NavigationSkeletonLoader";
import { DesktopFrequentlyUsedLinks } from "../DesktopFrequentlyUsedLinks/DesktopFrequentlyUsedLinks";
import commonContent from "../../../content/common";
import { isRouteActive } from "../../../common/util/Navigation/routeMatching";
import { ROUTE_PATHS } from "../../../common/enums/routing-enums";

interface SideNavigationPanelProps {
  navigationData?: NavigationApiResponse;
  isLoading?: boolean;
  isError?: boolean;
}

export const SideNavigationPanel = ({ navigationData, isLoading: _isLoading, isError }: SideNavigationPanelProps) => {
  const { t } = useTranslation(["navigation", "common"]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname, hash } = useLocation();
  const { headless } = useAppSelector((state) => state.loginLocalStorage);
  const { configuration, user } = useAppSelector((state) => state.boot);
  const { sideNavExpanded } = useAppSelector((state) => state.uiState);
  const [activeFlyout, setActiveFlyout] = useState<string | null>(null);
  const [flyoutPosition, setFlyoutPosition] = useState({ top: 0, left: 0 });
  const [flyoutOpenedViaKeyboard, setFlyoutOpenedViaKeyboard] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const navItemRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const touchOpenedFlyoutRef = useRef(false);
  const { handleAcademyClick } = useAcademyLink();

  const siteTitle = `${t("common:newSiteTitle", commonContent.newSiteTitle)}`;

  // Academy link setup
  const showAcademy = navigationData ? navigationData.Academy?.Id === "academy" : false;
  const academyBaseLink = configuration?.externalIntegration?.academy?.navigationlink ?? "";
  const academyLink = user?.cognitoToken ? `${academyBaseLink}?q=${user.cognitoToken}` : academyBaseLink;

  // Create navigation config from API data
  const navConfig = navigationData ? createNavigationConfig(navigationData, undefined, user.aff) : null;
  const showWhosNewIncludesCustomers = configuration?.actionReports?.showWhosNewIncludesCustomers ?? false;

  // Build category menus (excluding Academy since it's an external link)
  const categoryMenus = useMemo(
    () =>
      navConfig
        ? {
            performanceIncome: {
              title: `${t("performanceAndIncome", defaultContent.performanceAndIncome as string)}`,
              items: navConfig.performanceIncomeFlyout,
              dashboardPath: navConfig.performanceIncomeFlyout[0]?.path,
            },
            groupManagement: {
              title: `${t("groupManagement", defaultContent.groupManagement as string)}`,
              items: showWhosNewIncludesCustomers
                ? navConfig.groupManagementFlyout.map((section) => ({
                    ...section,
                    children: section.children?.map((child) =>
                      child.id === "whosNew" ? { ...child, contentKey: "whosNewWithCustomers" } : child,
                    ),
                  }))
                : navConfig.groupManagementFlyout,
              dashboardPath: navConfig.groupManagementFlyout[0]?.path,
            },
          }
        : null,
    [navConfig, showWhosNewIncludesCustomers, t],
  );

  // Convert flyout items to accordion items (preserve sections)
  const convertToAccordionItems = (
    items: NonNullable<typeof navConfig>["performanceIncomeFlyout"],
  ): AccordionMenuItem[] => {
    return items.map((item) => ({
      id: item.id,
      contentKey: item.contentKey,
      label: item.label,
      path: item.path,
      children: item.children,
      isSection: item.isSection,
    }));
  };

  // Helper to check if any item in a category matches the current route
  const isCategoryActive = useCallback(
    (categoryId: string): boolean => {
      if (!categoryMenus) return false;
      const menu = categoryMenus[categoryId as keyof typeof categoryMenus];
      if (!menu) return false;

      // Recursively check if any item or child item matches the current route
      const checkItems = (items: typeof menu.items): boolean => {
        return items.some((item) => {
          if (item.path && isRouteActive(item.path, pathname, hash)) {
            return true;
          }
          // Special case: Performance Plus and Performance Plus Elite are related
          // If checking performancePlus and we're on /performance-plus-elite, also match
          if (item.id === "performancePlus" && pathname === ROUTE_PATHS.PPE) {
            return true;
          }
          if (item.children) {
            return checkItems(item.children);
          }
          return false;
        });
      };

      return checkItems(menu.items);
    },
    [categoryMenus, pathname, hash],
  );

  const handleToggle = () => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!reducedMotion) {
      setIsAnimating(true);
    }
    dispatch(setSideNavExpanded(!sideNavExpanded));
  };

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLElement>) => {
    if (e.propertyName === "width") {
      setIsAnimating(false);
    }
  };

  const handleCategoryMouseEnter = useCallback(
    (categoryId: string, event: React.MouseEvent<HTMLAnchorElement>) => {
      if (!sideNavExpanded && categoryMenus) {
        const menu = categoryMenus[categoryId as keyof typeof categoryMenus];
        // Only show flyout if more than one item
        if (menu && menu.items.length > 1) {
          const rect = event.currentTarget.getBoundingClientRect();
          setFlyoutPosition({
            top: rect.top,
            left: rect.right + 8, // 0.5rem gap
          });
          setActiveFlyout(categoryId);
          setFlyoutOpenedViaKeyboard(false); // Opened via hover
        }
      }
    },
    [sideNavExpanded, categoryMenus],
  );

  const handleCategoryTouchStart = useCallback(
    (categoryId: string, event: React.TouchEvent<HTMLAnchorElement>) => {
      if (!sideNavExpanded && categoryMenus) {
        const menu = categoryMenus[categoryId as keyof typeof categoryMenus];
        // Only show flyout if more than one item
        if (menu && menu.items.length > 1) {
          event.preventDefault(); // Prevent mouse events and Link navigation
          event.stopPropagation(); // Stop event from bubbling
          const rect = event.currentTarget.getBoundingClientRect();
          setFlyoutPosition({
            top: rect.top,
            left: rect.right + 8, // 0.5rem gap
          });
          setActiveFlyout(categoryId);
          setFlyoutOpenedViaKeyboard(false); // Opened via touch
          touchOpenedFlyoutRef.current = true; // Mark that touch opened the flyout
        }
      }
    },
    [sideNavExpanded, categoryMenus],
  );

  const handleCategoryClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    // If flyout was just opened via touch, prevent navigation and let the flyout stay open
    if (touchOpenedFlyoutRef.current) {
      event.preventDefault();
      touchOpenedFlyoutRef.current = false;
    }
    // Otherwise, Link handles navigation
  }, []);

  const handleCategoryKeyDown = useCallback(
    (categoryId: string, event: React.KeyboardEvent<HTMLAnchorElement>) => {
      // Open flyout on Enter or Space when collapsed
      if (!sideNavExpanded && categoryMenus && (event.key === "Enter" || event.key === " ")) {
        const menu = categoryMenus[categoryId as keyof typeof categoryMenus];
        // Only show flyout if more than one item
        if (menu && menu.items.length > 1) {
          event.preventDefault(); // Prevent Link navigation
          const rect = event.currentTarget.getBoundingClientRect();
          setFlyoutPosition({
            top: rect.top,
            left: rect.right + 8, // 0.5rem gap
          });
          setActiveFlyout(categoryId);
          setFlyoutOpenedViaKeyboard(true); // Opened via keyboard
        }
        // If only one item, allow Link to navigate (don't preventDefault)
      }
    },
    [sideNavExpanded, categoryMenus],
  );

  const handleCategoryMouseLeave = useCallback(() => {
    // Don't close - flyout handles its own closing on mouse leave
  }, []);

  const handleFlyoutMouseEnter = useCallback(() => {
    // Keep flyout open when mouse is over it
  }, []);

  const handleFlyoutMouseLeave = useCallback(() => {
    setActiveFlyout(null);
  }, []);

  const handleCloseFlyout = useCallback(() => {
    setActiveFlyout(null);
    touchOpenedFlyoutRef.current = false; // Clear touch flag when flyout closes
  }, []);

  // Clear touch state when sidebar expands (flyouts are hidden in expanded mode)
  useEffect(() => {
    if (sideNavExpanded) {
      setActiveFlyout(null);
      touchOpenedFlyoutRef.current = false;
    }
  }, [sideNavExpanded]);

  // Log error if navigation query fails
  useEffect(() => {
    if (isError) {
      // eslint-disable-next-line no-console
      console.error("Failed to load navigation data");
    }
  }, [isError]);

  if (headless) return null;

  return (
    <nav
      className={`SideNavigationPanel ${!sideNavExpanded ? "SideNavigationPanel--collapsed" : ""} ${activeFlyout ? "SideNavigationPanel--flyout-active" : ""} ${isAnimating ? "SideNavigationPanel--animating" : ""}`}
      aria-label={sideNavExpanded ? "Main navigation" : "Main navigation, collapsed"}
      data-testid="side-navigation-panel"
      onTransitionEnd={handleTransitionEnd}
    >
      <div className="SideNavigationPanel__header">
        <div className="SideNavigationPanel__logo">
          <Link
            to="/performance-dashboard"
            className="SideNavigationPanel__logo-icon"
            aria-label={t("logoHome", defaultContent.logoHome as string)}
          >
            <IconAmwayLogo />
          </Link>
          <span className="SideNavigationPanel__logo-text">{siteTitle}</span>
        </div>
        <button
          type="button"
          className="SideNavigationPanel__expand-button"
          onClick={handleToggle}
          aria-label={sideNavExpanded ? "Collapse navigation" : "Expand navigation"}
          aria-expanded={sideNavExpanded}
          data-testid="side-nav-expand-button"
        >
          <NavExpandIcon />
        </button>
      </div>

      <div className="SideNavigationPanel__scroll-area">
        <div className="SideNavigationPanel__main-nav">
          {!navigationData ? (
            <NavigationSkeletonLoader />
          ) : sideNavExpanded ? (
            // Expanded state: Show accordions
            <>
              {categoryMenus?.performanceIncome && categoryMenus.performanceIncome.items.length > 0 && (
                <CategoryAccordion
                  categoryId="performanceIncome"
                  title={categoryMenus.performanceIncome.title}
                  items={convertToAccordionItems(categoryMenus.performanceIncome.items)}
                  icon={<IconPerfIncome active={false} />}
                  defaultExpanded={false}
                  isActive={isCategoryActive("performanceIncome")}
                />
              )}
              {categoryMenus?.groupManagement && categoryMenus.groupManagement.items.length > 0 && (
                <CategoryAccordion
                  categoryId="groupManagement"
                  title={categoryMenus.groupManagement.title}
                  items={convertToAccordionItems(categoryMenus.groupManagement.items)}
                  icon={<IconTwoUsersNav color="white" />}
                  defaultExpanded={false}
                  isActive={isCategoryActive("groupManagement")}
                />
              )}
              {showAcademy && (
                <a
                  href={academyLink}
                  target="academyFrame"
                  rel="noopener noreferrer"
                  className="SideNavigationPanel__nav-item SideNavigationPanel__nav-item--link"
                  onClick={(e) => handleAcademyClick(academyLink, e)}
                >
                  <div className="SideNavigationPanel__nav-item-icon">
                    <IconAcademy color="white" />
                  </div>
                  <span className="SideNavigationPanel__nav-item-label">
                    {t("academy", defaultContent.academy as string)}
                  </span>
                </a>
              )}
            </>
          ) : (
            // Collapsed state: Show icon buttons
            <>
              {categoryMenus?.performanceIncome &&
                categoryMenus.performanceIncome.items.length > 0 &&
                (() => {
                  const menu = categoryMenus.performanceIncome;
                  const [pathname, hash] = (menu.dashboardPath ?? "").split("#");
                  return (
                    <Link
                      ref={(el) => {
                        navItemRefs.current["performanceIncome"] = el;
                      }}
                      to={{ pathname, hash: hash ? `#${hash}` : undefined }}
                      className={`SideNavigationPanel__nav-item SideNavigationPanel__hover-circle ${isCategoryActive("performanceIncome") ? "SideNavigationPanel__nav-item--active" : ""}`}
                      aria-label={menu.title}
                      aria-haspopup={menu.items.length > 1 ? "true" : undefined}
                      aria-expanded={menu.items.length > 1 ? activeFlyout === "performanceIncome" : undefined}
                      data-testid="side-nav-performance-income"
                      onClick={handleCategoryClick}
                      onKeyDown={(e) => handleCategoryKeyDown("performanceIncome", e)}
                      onMouseEnter={(e) => handleCategoryMouseEnter("performanceIncome", e)}
                      onMouseLeave={handleCategoryMouseLeave}
                      onTouchStart={(e) => handleCategoryTouchStart("performanceIncome", e)}
                    >
                      <div className="SideNavigationPanel__nav-item-icon">
                        <IconPerfIncome active={false} />
                      </div>
                      <span className="SideNavigationPanel__nav-item-label" aria-hidden="true">
                        {menu.title}
                      </span>
                      <div className="SideNavigationPanel__nav-item-chevron" aria-hidden="true">
                        <IconChevronDown color="white" />
                      </div>
                    </Link>
                  );
                })()}
              {categoryMenus?.groupManagement &&
                categoryMenus.groupManagement.items.length > 0 &&
                (() => {
                  const menu = categoryMenus.groupManagement;
                  const [pathname, hash] = (menu.dashboardPath ?? "").split("#");
                  return (
                    <Link
                      ref={(el) => {
                        navItemRefs.current["groupManagement"] = el;
                      }}
                      to={{ pathname, hash: hash ? `#${hash}` : undefined }}
                      className={`SideNavigationPanel__nav-item SideNavigationPanel__hover-circle ${isCategoryActive("groupManagement") ? "SideNavigationPanel__nav-item--active" : ""}`}
                      aria-label={menu.title}
                      aria-haspopup={menu.items.length > 1 ? "true" : undefined}
                      aria-expanded={menu.items.length > 1 ? activeFlyout === "groupManagement" : undefined}
                      data-testid="nav-group-management"
                      onClick={handleCategoryClick}
                      onKeyDown={(e) => handleCategoryKeyDown("groupManagement", e)}
                      onMouseEnter={(e) => handleCategoryMouseEnter("groupManagement", e)}
                      onMouseLeave={handleCategoryMouseLeave}
                      onTouchStart={(e) => handleCategoryTouchStart("groupManagement", e)}
                    >
                      <div className="SideNavigationPanel__nav-item-icon">
                        <IconTwoUsersNav color="white" />
                      </div>
                      <span className="SideNavigationPanel__nav-item-label" aria-hidden="true">
                        {menu.title}
                      </span>
                      <div className="SideNavigationPanel__nav-item-chevron" aria-hidden="true">
                        <IconChevronDown color="white" />
                      </div>
                    </Link>
                  );
                })()}
              {showAcademy && (
                <a
                  href={academyLink}
                  target="academyFrame"
                  rel="noopener noreferrer"
                  className="SideNavigationPanel__nav-item SideNavigationPanel__hover-circle"
                  aria-label={t("academy", defaultContent.academy as string)}
                  onClick={(e) => handleAcademyClick(academyLink, e)}
                  onMouseEnter={handleCloseFlyout}
                >
                  <div className="SideNavigationPanel__nav-item-icon">
                    <IconAcademy color="white" />
                  </div>
                </a>
              )}
            </>
          )}
        </div>

        <div className="SideNavigationPanel__quick-links-section">
          <div className="SideNavigationPanel__quick-links-header">
            <div className="SideNavigationPanel__divider" />
            <div className="SideNavigationPanel__quick-links-title">
              {t("frequentlyUsed", defaultContent.frequentlyUsed as string)}
            </div>
          </div>

          {!sideNavExpanded && <div className="SideNavigationPanel__divider" />}

          <div className="SideNavigationPanel__quick-links">
            <DesktopFrequentlyUsedLinks navigationData={navigationData} />
          </div>
        </div>
      </div>

      {activeFlyout &&
        !sideNavExpanded &&
        categoryMenus &&
        categoryMenus[activeFlyout as keyof typeof categoryMenus] && (
          <CategoryFlyoutMenu
            title={categoryMenus[activeFlyout as keyof typeof categoryMenus]!.title}
            items={categoryMenus[activeFlyout as keyof typeof categoryMenus]!.items}
            position={flyoutPosition}
            onClose={handleCloseFlyout}
            onMouseEnter={handleFlyoutMouseEnter}
            onMouseLeave={handleFlyoutMouseLeave}
            triggerButtonRef={navItemRefs.current[activeFlyout]}
            openedViaKeyboard={flyoutOpenedViaKeyboard}
          />
        )}
    </nav>
  );
};
